<script setup lang="ts">
import { promiseTimeout, useDebounceFn } from '@vueuse/core'
import type { Expedition } from '~/shared/expedition'
import type { CekAjaT, PricingResponseT } from '~/types/cekaja'

const showDialog = ref(false)
const result = ref<PricingResponseT[]>()

const props = withDefaults(
  defineProps<{
    title?: string
    description?: string
    priority?: string
    courier?: Expedition
  }>(),
  {
    title: 'Cek Ongkir dari Berbagai Ekspedisi',
    description:
      'Lebih mudah cek tarif pengiriman dari berbagai <br/> ekspedisi dengan KiriminAja.',
    priority: undefined,
    courier: undefined
  }
)

const showResult = ref(false)
const { $toast, $recaptcha } = useNuxtApp()
const loading = ref(false)

const onSubmit = (call: CekAjaT) => {
  useDebounceFn(async () => {
    loading.value = true
    result.value = []
    showResult.value = false

    $fetch<{
      name: string
      message?: string
      data?: PricingResponseT[]
    }>('/intl-api/pricing', {
      method: 'POST',
      body: {
        from: call.origin?.toString() ?? '',
        thru: call.destination?.toString() ?? '',
        weight: call.weight?.toString() ?? '',
        width: call.width?.toString() ?? '',
        height: call.height?.toString() ?? '',
        length: call.length?.toString() ?? '',
        captcha: await $recaptcha?.getToken('cekaja')
      }
    })
      .then((response) => {
        showDialog.value = true
        loading.value = false
        showResult.value = true
        sortingCode.value = 'regular'
        if (response.name === 'SUCCESS' && response.data) {
          result.value = response.data ?? []
          promiseTimeout(300).then(() => {
            window?.scrollTo({
              top: (resultRef.value?.$el?.offsetTop ?? 0) - 90,
              behavior: 'smooth'
            })
          })
          return true
        } else {
          $toast.error(
            response?.message || 'Tidak ada hasil yang ditemukan',
            undefined,
            'Gagal mendapatkan ongkir'
          )
          return false
        }
      })
      .catch((error) => {
        loading.value = false
        $toast.error(
          error?.data?.message || 'Terjadi kesalahan saat mengambil data ongkir'
        )
        return false
      })
  }, 500)()
}

const resultRef = useTemplateRef('resultRef')
const sortingCode = ref('regular')
const sortingGroup = {
  regular: 'Reguler',
  one_day: 'One Day',
  next_day: 'Next Day',
  economy: 'Ekonomi',
  cargo: 'Kargo'
}
const sortingOptions = computed(() => {
  if (!result.value) return []

  const seen = new Set()
  return Object.entries(sortingGroup)
    .filter(([value]) => {
      if (seen.has(value)) return false
      const hasItem =
        result.value?.some((item) => item.group === value) ?? false
      if (hasItem) seen.add(value)
      return hasItem
    })
    .map(([value, label]) => ({ value, label }))
})

// Memoize sorted results to prevent re-sorting on every render
const sortedCourier = computed(() => {
  if (!result.value?.length) return []

  // Single pass: filter and sort together
  return result.value
    .filter((item) => item.group === sortingCode.value)
    .sort((a, b) => {
      const costA =
        Number(a.cost) -
        (a.discount_campaign.discount > 0
          ? Number(a.discount_campaign.discount)
          : 0)
      const costB =
        Number(b.cost) -
        (b.discount_campaign.discount > 0
          ? Number(b.discount_campaign.discount)
          : 0)
      return costA - costB
    })
})

const prioritizedCourier = computed(() => {
  if (!props.priority || !sortedCourier.value.length) return []
  return sortedCourier.value.filter((item) => item.service === props.priority)
})

const mostValuableCourier = computed(() => {
  if (!props.priority) return sortedCourier.value
  if (!sortedCourier.value.length) return []
  return sortedCourier.value.filter((item) => item.service !== props.priority)
})

const cekAjaForm = useTemplateRef('cekAjaForm')

const cekAjaStore = useCekAjaStore()
const { setLoadOnMount } = cekAjaStore
const { getLoadOnMount } = storeToRefs(cekAjaStore)

onMounted(async () => {
  if (getLoadOnMount.value) {
    setLoadOnMount(false)
    cekAjaForm.value?.onSubmit()
  }
})

const slots = useSlots()
</script>

<template>
  <HeroBase
    :title="props.title"
    :description="props.description"
    title-as="h1"
    hero-image="/images/home-v4/cek-ongkir.avif"
  >
    <template v-if="courier" #above-title>
      <div class="flex gap-3 items-center">
        <UiCourierService
          class="h-6 aspect-video"
          :icon="courier?.icon"
          icon-size="40px"
          :class="courier.class"
        />
        <div class="grow font-semibold">
          {{ courier.courier }}
        </div>
      </div>
    </template>

    <template v-if="slots.hero" #hero>
      <slot name="hero" />
    </template>
    <template #action>
      <CekajaForm
        ref="cekAjaForm"
        class="mt-5"
        expand
        :loading="loading"
        @submit="onSubmit"
      />
    </template>
  </HeroBase>
  <section v-if="showResult" class="py-6 lg:py-10">
    <UiContainer>
      <template v-if="sortedCourier?.length">
        <UiTabBase
          v-if="sortingOptions.length > 1"
          ref="resultRef"
          v-model="sortingCode"
          :tabs="sortingOptions"
          wrapper-class="border-none inline-flex rounded-full mb-6 gap-3"
          trigger-class="w-24 data-[state=active]:text-primary p-2"
          indicator-wrapper-class="h-full translate-y-0 px-0"
        >
          <template #indicator>
            <div class="bg-secondary-container w-full h-full rounded-full" />
          </template>
        </UiTabBase>
        <div class="space-y-3">
          <div v-if="prioritizedCourier?.length">
            <h3 class="text-lg font-bold mb-3">Hasil Pencarian</h3>
            <div class="grid gap-3">
              <UiCourierItem
                v-for="(item, index) in prioritizedCourier"
                :key="index"
                :item="item"
                priority
              />
            </div>
          </div>

          <div
            id="result-info"
            class="relative bg-primary w-full rounded-xl border-4 md:border-0 border-primary-container overflow-hidden md:before:content-[''] md:before:absolute md:before:-left-0.5 md:before:top-1/2 md:before:-translate-y-1/2 md:before:w-2 md:before:h-4/5 md:before:bg-primary-container md:before:rounded-full md:before:z-10"
          >
            <div
              class="flex flex-col md:flex-row md:items-center gap-4 text-on-primary px-8 py-4 z-10 relative"
            >
              <NuxtImg
                class="flex-none w-20"
                src="/assets/home-v4/cek-aja/verified.png"
                alt="verified-illust"
              />
              <div class="flex-1">
                <div class="text-sm md:text-base font-semibold">
                  Makin Untung dengan Akun Terverifikasi!
                </div>
                <div class="text-xs md:text-sm">
                  Diskon yang ditampilkan adalah diskon dasar. Daftar dan
                  verifikasi akunmu untuk dapat promo lebih banyak.
                </div>
              </div>
              <UiButton
                class="flex-none text-primary px-8"
                variant="primaryContainer"
                label="Daftar Sekarang"
                :to="$utm.utmUrl()"
              />
            </div>
            <NuxtImg
              class="absolute left-1/2 top-1/2 md:top-12 h-full -translate-x-1/2 -translate-y-1/2 scale-150 sm:scale-100 md:scale-200 dark:opacity-50"
              src="/assets/home-v4/cek-aja/alert-pattern-1.svg"
              alt="illust-pattern"
            />
            <NuxtImg
              class="absolute left-0 top-0 w-full md:w-1/3 dark:opacity-50"
              src="/assets/home-v4/cek-aja/alert-pattern-2.svg"
              alt="illust-pattern"
            />
            <NuxtImg
              class="absolute right-0 bottom-0 w-full md:w-1/3 dark:opacity-50"
              src="/assets/home-v4/cek-aja/alert-pattern-3.svg"
              alt="illust-pattern"
            />
          </div>

          <div>
            <h3 class="text-lg font-bold mb-3">Ekspedisi Lainnya</h3>
            <div class="grid gap-3">
              <UiCourierItem
                v-for="(item, index) in mostValuableCourier"
                :key="index"
                :item="item"
              />
            </div>
          </div>
        </div>
      </template>
      <UiAlert
        v-else
        icon="material-symbols:info-outline"
        variant="error"
        title="Tidak Ditemukan Hasil"
        description="Maaf, tidak ada hasil yang ditemukan untuk rute pengiriman ini."
      />
    </UiContainer>
  </section>
</template>
